<template>
  <div class="checkpoint-card-actions" role="toolbar" aria-label="Checkpoint actions">
    <div
      v-if="has_changes"
      class="checkpoint-card-actions__edit-hit"
      role="button"
      tabindex="0"
      aria-label="Edit checkpoint"
      @click="$emit('edit')"
      @keydown.enter.prevent="$emit('edit')"
      @keydown.space.prevent="$emit('edit')"
    >
      <img :src="editIcon" alt="" />
    </div>

    <OrgObjectStatusBadge :status="status" class="checkpoint-card-actions__status" />

    <div class="checkpoint-card-actions__chatter" @click="$emit('chatter')">
      <ChatterIcon />
    </div>

    <CheckpointDropDownMenu
      :key="`checkpoint-menu-${menuData.id ?? status}`"
      :status="menuStatus"
      :menu-open="false"
      :horizontal="true"
      :activate-disabled="activateMenuDisabled"
      :locked="operationsMenuLocked"
      @action="handleCheckpointMenuAction($event)"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import editIcon from "@components/assets/edit-03.svg";
import CheckpointDropDownMenu from "@components/CheckpointDropDownMenu/CheckpointDropDownMenu.vue";
import store from "../../account/src/store";
import { isCheckpointActivateDisabled } from "@/utils/draftCheckpointValidity";
import { isCheckpointEditLockHeld } from "@/utils/checkpointEditLock";

const props = defineProps({
  has_changes: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: "active",
  },
  /**
   * Optional full checkpoint object so menu actions can be emitted
   * in the same shape as EditCheckpointsTable (`item`, `data`).
   */
  checkpointData: {
    type: Object,
    default: null,
  },
  /** When set, overrides MDP-452 activate disable logic (e.g. checkpoint edit form header). */
  activateDisabled: {
    type: Boolean,
    default: null,
  },
});

const ideaMDP452 = computed(() => Boolean(store.getters["featureFlags/ideaMDP452"]));

const activateMenuDisabled = computed(() => {
  if (props.activateDisabled !== null && props.activateDisabled !== undefined) {
    return Boolean(props.activateDisabled);
  }
  if (!ideaMDP452.value) {
    return false;
  }
  return isCheckpointActivateDisabled(menuData.value);
});

const emit = defineEmits(["edit", "chatter", "menu"]);

const menuData = computed(() => {
  if (props.checkpointData) return props.checkpointData;
  return {
    status: props.status,
  };
});

function capitalizeStatus(status, data) {
  if (
    data &&
    (data.archived === true || (status && status.toString().toLowerCase() === "archived"))
  ) {
    return "Archived";
  }
  if (!status) return "Draft";
  const normalized = status.toString();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

const menuStatus = computed(() => capitalizeStatus(menuData.value?.status, menuData.value));

/**
 * ER-11869: the operations menu lock affordance must remain driven solely by the edit lock
 * (a pending upload_microlearning_video task only disables Activate, it must not hide the
 * lock icon — otherwise the assignee cannot reach lockedBySelf to Cancel/Reassign or edit).
 */
const operationsMenuLocked = computed(() => isCheckpointEditLockHeld(menuData.value));

function handleCheckpointMenuAction(item) {
  if (item?.disabled || (item?.value === "activate" && activateMenuDisabled.value)) {
    return;
  }
  emit("menu", {
    item,
    data: menuData.value,
    showDialog: true,
  });
}
</script>

<style scoped>
.checkpoint-card-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-wrap: nowrap;
}

.checkpoint-card-actions__status {
  flex-shrink: 0;
}

.checkpoint-card-actions__edit-hit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  cursor: url("../assets/cursorArrow.svg"), auto;
  border-radius: 8px;
  padding: 4px;
}

.checkpoint-card-actions__edit-hit:hover {
  background: rgba(16, 24, 40, 0.05);
}

.checkpoint-card-actions__edit-hit:focus-visible {
  outline: 2px solid rgba(127, 86, 217, 0.45);
  outline-offset: 2px;
}

.checkpoint-card-actions__edit-img {
  display: block;
  flex-shrink: 0;
}

.checkpoint-card-actions__chatter {
  flex-shrink: 0;
}

.checkpoint-card-actions__menu {
  flex-shrink: 0;
}
::v-deep .ex-menu-icon:hover {
  background: none !important;
  padding: 8px;
  text-align: center !important;
  margin: 0px !important;
  box-shadow: none !important;
  border-radius: 0px !important;
}
::v-deep .ex-menu-icon {
  background: none !important;
  padding: 8px;
  text-align: center !important;
  margin: 0px !important;
  box-shadow: none !important;
  border-radius: 0px !important;
}
</style>
